import { calculatePortfolioMetrics, formatMoney, formatPercent } from "@seed-factory/core";
import {
  AlertTriangle,
  CheckCircle2,
  CircleDollarSign,
  Clock3,
  Gauge,
  Rocket,
  ShieldCheck,
  Target,
  UsersRound,
  XCircle,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { ChartCard } from "../components/ChartCard";
import { MetricCard } from "../components/MetricCard";
import { PageHeader } from "../components/PageHeader";
import { EmptyState, ErrorState, LoadingState } from "../components/StateViews";
import { StatusPill } from "../components/StatusPill";
import { useFilters } from "../contexts/FiltersContext";
import { buildSeries } from "../lib/analytics";
import { buildFunnel } from "../lib/funnel";
import { useEstufaData } from "../lib/queries";

const tabs = [
  "Overview",
  "Funil",
  "Receita",
  "Retenção & Churn",
  "Uso / Features",
  "Confiabilidade",
  "Suporte & Reviews",
  "Releases & Experimentos",
  "Decisões & Aprendizados",
  "Dados",
] as const;
type Tab = (typeof tabs)[number];

export function SeedPage() {
  const { seedId } = useParams();
  const { filter, setSeedId } = useFilters();
  const [tab, setTab] = useState<Tab>("Overview");
  useEffect(() => {
    if (seedId && filter.seedId !== seedId) setSeedId(seedId);
  }, [seedId, filter.seedId, setSeedId]);
  const query = useEstufaData();
  if (query.isLoading) return <LoadingState />;
  if (query.isError || !query.data) return <ErrorState />;
  const data = query.data;
  const seed = data.seeds.find((item) => item.id === seedId);
  if (!seed)
    return (
      <EmptyState
        title="Seed não encontrada"
        description="Verifique o filtro ou o identificador da rota."
      />
    );
  const deploymentIds = data.deployments
    .filter((item) => item.seedId === seed.id)
    .map((item) => item.id);
  const installations = data.installations.filter((item) =>
    deploymentIds.includes(item.deploymentId),
  );
  const subscriptions = data.subscriptions.filter((item) =>
    deploymentIds.includes(item.deploymentId),
  );
  const revenue = data.revenue.filter((item) => deploymentIds.includes(item.deploymentId));
  const events = data.events.filter((item) => deploymentIds.includes(item.deploymentId));
  const metricSnapshots = data.metricSnapshots.filter((item) =>
    deploymentIds.includes(item.deploymentId),
  );
  const metrics = calculatePortfolioMetrics({
    installations,
    subscriptions,
    revenue,
    events,
    from: filter.from,
    to: filter.to,
  });
  const series = buildSeries({
    installations,
    subscriptions,
    revenue,
    from: filter.from,
    to: filter.to,
    granularity: filter.granularity,
  });

  return (
    <>
      <PageHeader
        eyebrow={seed.seedKey}
        title={seed.name}
        description={seed.summary ?? undefined}
        action={<StatusPill value={seed.status} />}
      />
      <div className="tabs-scroller">
        <div className="tabs">
          {tabs.map((item) => (
            <button
              type="button"
              key={item}
              onClick={() => setTab(item)}
              className={tab === item ? "tab tab--active" : "tab"}
            >
              {item}
            </button>
          ))}
        </div>
      </div>
      {tab === "Overview" ? (
        <SeedOverview
          metrics={metrics}
          series={series}
          openIncidents={
            data.incidents.filter(
              (item) =>
                item.deploymentId &&
                deploymentIds.includes(item.deploymentId) &&
                item.status !== "resolved",
            ).length
          }
        />
      ) : null}
      {tab === "Funil" ? (
        <FunnelTab
          installations={installations}
          subscriptions={subscriptions}
          events={events}
          metricSnapshots={metricSnapshots}
          from={filter.from}
          to={filter.to}
        />
      ) : null}
      {tab === "Receita" ? (
        <RevenueTab series={series} subscriptions={subscriptions} metrics={metrics} />
      ) : null}
      {tab === "Retenção & Churn" ? (
        <RetentionTab
          installations={installations}
          events={events}
          subscriptions={subscriptions}
          from={filter.from}
          to={filter.to}
        />
      ) : null}
      {tab === "Uso / Features" ? <FeatureTab events={events} /> : null}
      {tab === "Confiabilidade" ? (
        <ReliabilityTab
          events={events}
          incidents={data.incidents.filter(
            (item) => item.deploymentId && deploymentIds.includes(item.deploymentId),
          )}
        />
      ) : null}
      {tab === "Suporte & Reviews" ? (
        <SupportReviewsTab
          support={data.supportTickets.filter(
            (item) => !item.deploymentId || deploymentIds.includes(item.deploymentId),
          )}
          reviews={data.reviews.filter(
            (item) => !item.deploymentId || deploymentIds.includes(item.deploymentId),
          )}
          activeInstallations={metrics.activeInstallations}
        />
      ) : null}
      {tab === "Releases & Experimentos" ? (
        <ReleaseTab
          releases={data.releases.filter((item) => deploymentIds.includes(item.deploymentId))}
          experiments={data.experiments.filter(
            (item) => !item.deploymentId || deploymentIds.includes(item.deploymentId),
          )}
        />
      ) : null}
      {tab === "Decisões & Aprendizados" ? (
        <LearningTab
          decisions={data.decisions.filter((item) => !item.seedId || item.seedId === seed.id)}
          lessons={data.lessons.filter(
            (item) => !item.sourceSeedId || item.sourceSeedId === seed.id,
          )}
        />
      ) : null}
      {tab === "Dados" ? (
        <DataTab
          data={{
            installations,
            subscriptions,
            revenue,
            events,
            metricSnapshots,
            supportTickets: data.supportTickets.filter(
              (item) => !item.deploymentId || deploymentIds.includes(item.deploymentId),
            ),
            reviews: data.reviews.filter(
              (item) => !item.deploymentId || deploymentIds.includes(item.deploymentId),
            ),
            releases: data.releases.filter((item) => deploymentIds.includes(item.deploymentId)),
            incidents: data.incidents.filter(
              (item) => item.deploymentId && deploymentIds.includes(item.deploymentId),
            ),
          }}
        />
      ) : null}
    </>
  );
}

function SeedOverview({
  metrics,
  series,
  openIncidents,
}: {
  metrics: ReturnType<typeof calculatePortfolioMetrics>;
  series: ReturnType<typeof buildSeries>;
  openIncidents: number;
}) {
  return (
    <>
      <div className="metrics-grid metrics-grid--seed">
        <MetricCard
          label="Instalações ativas"
          value={String(metrics.activeInstallations)}
          icon={UsersRound}
          tone="violet"
        />
        <MetricCard
          label="Ativação"
          value={formatPercent(metrics.activationRate)}
          icon={Rocket}
          tone="blue"
        />
        <MetricCard
          label="First Value"
          value={formatPercent(metrics.firstValueRate)}
          icon={Target}
          tone="cyan"
        />
        <MetricCard
          label="MRR"
          value={formatMoney(metrics.mrrCents)}
          icon={CircleDollarSign}
          tone="emerald"
        />
        <MetricCard
          label="TTFV"
          value={
            metrics.medianTtfvMinutes === null
              ? "—"
              : `${Math.round(metrics.medianTtfvMinutes)} min`
          }
          icon={Clock3}
          tone="amber"
        />
        <MetricCard
          label="Incidentes"
          value={String(openIncidents)}
          icon={AlertTriangle}
          tone={openIncidents ? "coral" : "emerald"}
        />
      </div>
      <div className="dashboard-grid">
        <ChartCard
          title="Aquisição → valor"
          subtitle="Instalações, ativações e first value"
          className="chart-card--wide"
        >
          <ResponsiveContainer width="100%" height={310}>
            <BarChart data={series}>
              <CartesianGrid vertical={false} stroke="var(--border)" strokeDasharray="3 3" />
              <XAxis dataKey="label" axisLine={false} tickLine={false} />
              <YAxis axisLine={false} tickLine={false} />
              <Tooltip />
              <Legend />
              <Bar
                dataKey="installs"
                name="Instalações"
                fill="var(--violet)"
                radius={[6, 6, 0, 0]}
              />
              <Bar
                dataKey="activations"
                name="Ativações"
                fill="var(--blue)"
                radius={[6, 6, 0, 0]}
              />
              <Bar
                dataKey="firstValue"
                name="First Value"
                fill="var(--emerald)"
                radius={[6, 6, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="MRR" subtitle="Receita recorrente ativa">
          <ResponsiveContainer width="100%" height={310}>
            <AreaChart data={series}>
              <defs>
                <linearGradient id="seedMrr" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="var(--emerald)" stopOpacity={0.35} />
                  <stop offset="95%" stopColor="var(--emerald)" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid vertical={false} stroke="var(--border)" />
              <XAxis dataKey="label" axisLine={false} tickLine={false} />
              <YAxis axisLine={false} tickLine={false} />
              <Tooltip formatter={(v) => `R$ ${Number(v).toFixed(2)}`} />
              <Area
                dataKey="mrr"
                name="MRR"
                stroke="var(--emerald)"
                fill="url(#seedMrr)"
                strokeWidth={3}
              />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
    </>
  );
}

function FunnelTab({
  installations,
  subscriptions,
  events,
  metricSnapshots,
  from,
  to,
}: {
  installations: Parameters<typeof calculatePortfolioMetrics>[0]["installations"];
  subscriptions: Parameters<typeof calculatePortfolioMetrics>[0]["subscriptions"];
  events: Parameters<typeof calculatePortfolioMetrics>[0]["events"];
  metricSnapshots: Parameters<typeof buildFunnel>[0]["metricSnapshots"];
  from: Date;
  to: Date;
}) {
  const stages = buildFunnel(
    { installations, subscriptions, events, metricSnapshots },
    { from, to, granularity: "day" },
  );
  const labels = {
    impressions: "Impressões",
    listing_views: "Listing views",
    installs: "Instalações",
    activations: "Ativações",
    onboarding: "Onboarding",
    first_value: "First Value",
    trial: "Trial",
    paid: "Pago",
  } as const;
  const max = Math.max(0, ...stages.map((item) => item.value));
  return (
    <section className="chart-card">
      <div className="card-heading">
        <div>
          <h3>Funil de valor</h3>
          <p>Diagnóstico por quebra, não só faturamento.</p>
        </div>
      </div>
      <div className="funnel-list">
        {stages.map(({ stage, value, stepConversionRate }) => (
          <div className="funnel-row" key={stage}>
            <div className="funnel-label">
              <strong>{labels[stage]}</strong>
              <span>
                {value}{" "}
                {stepConversionRate === null ? "" : `· ${formatPercent(stepConversionRate)}`}
              </span>
            </div>
            <div className="funnel-track">
              <div
                className="funnel-fill"
                style={{ width: `${max > 0 ? Math.max(4, (value / max) * 100) : 0}%` }}
              />
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function RevenueTab({
  series,
  subscriptions,
  metrics,
}: {
  series: ReturnType<typeof buildSeries>;
  subscriptions: Parameters<typeof calculatePortfolioMetrics>[0]["subscriptions"];
  metrics: ReturnType<typeof calculatePortfolioMetrics>;
}) {
  return (
    <>
      <div className="metrics-grid metrics-grid--seed">
        <MetricCard
          label="MRR"
          value={formatMoney(metrics.mrrCents)}
          icon={CircleDollarSign}
          tone="emerald"
        />
        <MetricCard label="ARR" value={formatMoney(metrics.arrCents)} icon={Gauge} tone="violet" />
        <MetricCard
          label="Pagos ativos"
          value={String(metrics.paidCustomers)}
          icon={UsersRound}
          tone="blue"
        />
        <MetricCard
          label="Churn"
          value={formatPercent(metrics.logoChurnRate)}
          icon={XCircle}
          tone="coral"
        />
      </div>
      <ChartCard
        title="MRR e receita"
        subtitle={`${subscriptions.length} registros de assinatura no recorte consultável`}
      >
        <ResponsiveContainer width="100%" height={340}>
          <AreaChart data={series}>
            <CartesianGrid vertical={false} stroke="var(--border)" />
            <XAxis dataKey="label" axisLine={false} tickLine={false} />
            <YAxis axisLine={false} tickLine={false} />
            <Tooltip />
            <Legend />
            <Area dataKey="mrr" name="MRR (R$)" stroke="var(--violet)" fill="var(--violet-soft)" />
            <Area
              dataKey="revenue"
              name="Receita (R$)"
              stroke="var(--emerald)"
              fill="var(--emerald-soft)"
            />
          </AreaChart>
        </ResponsiveContainer>
      </ChartCard>
    </>
  );
}

function RetentionTab({
  installations,
  events,
  subscriptions,
  from,
  to,
}: {
  installations: Parameters<typeof calculatePortfolioMetrics>[0]["installations"];
  events: Parameters<typeof calculatePortfolioMetrics>[0]["events"];
  subscriptions: Parameters<typeof calculatePortfolioMetrics>[0]["subscriptions"];
  from: Date;
  to: Date;
}) {
  const metrics = calculatePortfolioMetrics({
    installations,
    subscriptions,
    events,
    revenue: [],
    from,
    to,
  });
  const retained = [7, 14, 30, 60, 90].map((days) => {
    const eligible = installations.filter(
      (row) => new Date(row.installedAt).getTime() + days * 86_400_000 <= to.getTime(),
    );
    const active = eligible.filter((row) =>
      events.some(
        (event) =>
          event.tenantId === row.tenantId &&
          new Date(event.occurredAt).getTime() >=
            new Date(row.installedAt).getTime() + days * 86_400_000,
      ),
    ).length;
    return {
      day: `D${days}`,
      rate: eligible.length ? active / eligible.length : null,
      eligible: eligible.length,
    };
  });
  return (
    <div className="dashboard-grid">
      <ChartCard
        title="Retenção por atividade"
        subtitle="Considera atividade observada após o marco de retenção"
        className="chart-card--wide"
      >
        <div className="retention-grid">
          {retained.map((item) => (
            <div className="retention-cell" key={item.day}>
              <strong>{item.day}</strong>
              <span>{formatPercent(item.rate)}</span>
              <small>{item.eligible} elegíveis</small>
            </div>
          ))}
        </div>
        {events.length === 0 ? (
          <p className="inline-note">
            Sem eventos de atividade no recorte, portanto a retenção não é inventada.
          </p>
        ) : null}
      </ChartCard>
      <ChartCard title="Churn logo" subtitle="Cancelados / pagos ativos no início">
        <div className="big-stat">
          <span>{formatPercent(metrics.logoChurnRate)}</span>
          <small>{metrics.churnedCustomers} cancelamentos no período</small>
        </div>
      </ChartCard>
    </div>
  );
}

function FeatureTab({
  events,
}: {
  events: Parameters<typeof calculatePortfolioMetrics>[0]["events"];
}) {
  const usage = useMemo(() => {
    const map = new Map<string, number>();
    for (const event of events.filter((item) => item.eventFamily === "feature_used")) {
      const name =
        typeof event.metadata?.feature === "string" ? event.metadata.feature : event.eventName;
      map.set(name, (map.get(name) ?? 0) + 1);
    }
    return [...map.entries()]
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);
  }, [events]);
  if (usage.length === 0)
    return (
      <EmptyState
        title="Uso de features ainda não instrumentado"
        description="A Estufa não deduz uso. Eventos `feature_used` preencherão esta tela."
      />
    );
  return (
    <ChartCard title="Uso das features" subtitle="Eventos de uso no período">
      <ResponsiveContainer width="100%" height={360}>
        <BarChart data={usage} layout="vertical">
          <CartesianGrid horizontal={false} stroke="var(--border)" />
          <XAxis type="number" axisLine={false} tickLine={false} />
          <YAxis type="category" dataKey="name" width={150} axisLine={false} tickLine={false} />
          <Tooltip />
          <Bar dataKey="value" name="Usos" fill="var(--cyan)" radius={[0, 7, 7, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

function ReliabilityTab({
  events,
  incidents,
}: {
  events: Parameters<typeof calculatePortfolioMetrics>[0]["events"];
  incidents: { id: string; status: string; severity: string; title: string }[];
}) {
  const succeeded = events.filter((item) => item.eventFamily === "core_succeeded").length;
  const failed = events.filter((item) => item.eventFamily === "core_failed").length;
  const blocked = events.filter((item) => item.eventFamily === "core_blocked").length;
  const attempts = succeeded + failed;
  const reliability = attempts > 0 ? succeeded / attempts : null;
  return (
    <>
      <div className="metrics-grid metrics-grid--seed">
        <MetricCard
          label="Sucesso técnico"
          value={formatPercent(reliability)}
          icon={ShieldCheck}
          tone="emerald"
        />
        <MetricCard label="Sucessos" value={String(succeeded)} icon={CheckCircle2} tone="blue" />
        <MetricCard label="Falhas técnicas" value={String(failed)} icon={XCircle} tone="coral" />
        <MetricCard
          label="Bloqueios esperados"
          value={String(blocked)}
          icon={Target}
          tone="amber"
        />
      </div>
      <section className="table-card">
        <div className="card-heading">
          <div>
            <h3>Incidentes</h3>
            <p>Blocked de negócio não é confundido com bug.</p>
          </div>
        </div>
        {incidents.length === 0 ? (
          <EmptyState title="Sem incidentes no recorte" />
        ) : (
          <div className="card-list">
            {incidents.map((item) => (
              <div className="list-row" key={item.id}>
                <AlertTriangle size={17} />
                <div>
                  <strong>{item.title}</strong>
                  <span>{item.severity}</span>
                </div>
                <StatusPill value={item.status} />
              </div>
            ))}
          </div>
        )}
      </section>
    </>
  );
}

function SupportReviewsTab({
  support,
  reviews,
  activeInstallations,
}: {
  support: {
    id: string;
    category: string | null;
    severity: string | null;
    openedAt: string;
    resolvedAt: string | null;
    firstResponseMinutes: number | null;
    resolutionMinutes: number | null;
  }[];
  reviews: { id: string; rating: number | null; reviewedAt: string; categoryTags: string[] }[];
  activeInstallations: number;
}) {
  const open = support.filter((ticket) => !ticket.resolvedAt).length;
  const supportRate = activeInstallations > 0 ? (support.length / activeInstallations) * 100 : null;
  const ratings = reviews.flatMap((review) => (review.rating === null ? [] : [review.rating]));
  const average = ratings.length
    ? ratings.reduce((sum, value) => sum + value, 0) / ratings.length
    : null;
  const firstResponse = support.flatMap((ticket) =>
    ticket.firstResponseMinutes === null ? [] : [ticket.firstResponseMinutes],
  );
  const avgFirstResponse = firstResponse.length
    ? firstResponse.reduce((sum, value) => sum + value, 0) / firstResponse.length
    : null;
  return (
    <>
      <div className="metrics-grid metrics-grid--seed">
        <MetricCard
          label="Tickets"
          value={String(support.length)}
          icon={AlertTriangle}
          tone="amber"
          hint={`${open} abertos`}
        />
        <MetricCard
          label="Tickets / 100 ativos"
          value={supportRate === null ? "—" : supportRate.toFixed(1)}
          icon={Gauge}
          tone="blue"
        />
        <MetricCard
          label="1ª resposta média"
          value={avgFirstResponse === null ? "—" : `${Math.round(avgFirstResponse)} min`}
          icon={Clock3}
          tone="cyan"
        />
        <MetricCard
          label="Rating médio"
          value={average === null ? "—" : average.toFixed(2)}
          icon={Target}
          tone="emerald"
          hint={`${reviews.length} reviews`}
        />
      </div>
      <div className="two-column-content">
        <section className="table-card">
          <div className="card-heading">
            <div>
              <h3>Suporte</h3>
              <p>Fricção e bugs vistos pelo cliente.</p>
            </div>
          </div>
          {support.length === 0 ? (
            <EmptyState title="Sem tickets registrados" />
          ) : (
            <div className="card-list">
              {support.map((ticket) => (
                <div className="list-row" key={ticket.id}>
                  <AlertTriangle size={16} />
                  <div>
                    <strong>{ticket.category ?? "Sem categoria"}</strong>
                    <span>
                      {ticket.severity ?? "—"} ·{" "}
                      {new Date(ticket.openedAt).toLocaleDateString("pt-BR")}
                    </span>
                  </div>
                  <StatusPill value={ticket.resolvedAt ? "resolved" : "open"} />
                </div>
              ))}
            </div>
          )}
        </section>
        <section className="table-card">
          <div className="card-heading">
            <div>
              <h3>Reviews</h3>
              <p>Rating e padrões sem inferir receita.</p>
            </div>
          </div>
          {reviews.length === 0 ? (
            <EmptyState title="Sem reviews registradas" />
          ) : (
            <div className="card-list">
              {reviews.map((review) => (
                <div className="list-row list-row--stack" key={review.id}>
                  <strong>{review.rating ?? "—"} / 5</strong>
                  <span>{new Date(review.reviewedAt).toLocaleDateString("pt-BR")}</span>
                  <small>{review.categoryTags.join(" · ") || "Sem tags"}</small>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </>
  );
}

function ReleaseTab({
  releases,
  experiments,
}: {
  releases: {
    id: string;
    version: string;
    summary: string;
    releasedAt: string;
    riskLevel: string;
    status: string;
    commitSha: string | null;
  }[];
  experiments: {
    id: string;
    name: string;
    hypothesis: string;
    primaryMetric: string;
    verdict: string | null;
  }[];
}) {
  return (
    <div className="two-column-content">
      <section className="table-card">
        <div className="card-heading">
          <div>
            <h3>Releases</h3>
            <p>Histórico para comparar antes x depois sem declarar causalidade.</p>
          </div>
        </div>
        {releases.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="timeline">
            {releases.map((release) => (
              <div className="timeline-item" key={release.id}>
                <div className="timeline-dot" />
                <div>
                  <div className="timeline-title">
                    <strong>{release.version}</strong>
                    <StatusPill value={release.status} />
                  </div>
                  <p>{release.summary}</p>
                  <small>
                    {new Date(release.releasedAt).toLocaleString("pt-BR")} · risco{" "}
                    {release.riskLevel}
                    {release.commitSha ? ` · ${release.commitSha.slice(0, 7)}` : ""}
                  </small>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
      <section className="table-card">
        <div className="card-heading">
          <div>
            <h3>Experimentos</h3>
            <p>Hipóteses com métrica primária e verdict explícito.</p>
          </div>
        </div>
        {experiments.length === 0 ? (
          <EmptyState title="Nenhum experimento registrado" />
        ) : (
          <div className="card-list">
            {experiments.map((experiment) => (
              <div className="list-row list-row--stack" key={experiment.id}>
                <strong>{experiment.name}</strong>
                <span>{experiment.hypothesis}</span>
                <small>Métrica: {experiment.primaryMetric}</small>
                {experiment.verdict ? <StatusPill value={experiment.verdict} /> : null}
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function LearningTab({
  decisions,
  lessons,
}: {
  decisions: {
    id: string;
    title: string;
    category: string;
    rationale: string;
    verdict: string | null;
    confidence: number | null;
  }[];
  lessons: {
    id: string;
    lesson: string;
    evidenceType: string;
    whatNotToRepeat: string | null;
    whatToRepeat: string | null;
    confidence: number | null;
  }[];
}) {
  return (
    <div className="two-column-content">
      <section className="table-card">
        <div className="card-heading">
          <div>
            <h3>Decisões</h3>
            <p>O que mudamos e por quê.</p>
          </div>
        </div>
        {decisions.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="card-list">
            {decisions.map((decision) => (
              <div className="learning-card" key={decision.id}>
                <div className="learning-meta">
                  <span>{decision.category}</span>
                  {decision.verdict ? (
                    <StatusPill value={decision.verdict} />
                  ) : (
                    <span>sem verdict</span>
                  )}
                </div>
                <strong>{decision.title}</strong>
                <p>{decision.rationale}</p>
                <small>Confiança: {decision.confidence ?? "—"}%</small>
              </div>
            ))}
          </div>
        )}
      </section>
      <section className="table-card">
        <div className="card-heading">
          <div>
            <h3>Aprendizados</h3>
            <p>A memória acumulada da fábrica.</p>
          </div>
        </div>
        {lessons.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="card-list">
            {lessons.map((lesson) => (
              <div className="learning-card" key={lesson.id}>
                <div className="learning-meta">
                  <span>{lesson.evidenceType}</span>
                  <span>{lesson.confidence ?? "—"}% confiança</span>
                </div>
                <strong>{lesson.lesson}</strong>
                {lesson.whatToRepeat ? (
                  <p>
                    <b>Repetir:</b> {lesson.whatToRepeat}
                  </p>
                ) : null}
                {lesson.whatNotToRepeat ? (
                  <p>
                    <b>Evitar:</b> {lesson.whatNotToRepeat}
                  </p>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function DataTab({ data }: { data: Record<string, unknown[]> }) {
  return (
    <section className="table-card">
      <div className="card-heading">
        <div>
          <h3>Dados do recorte</h3>
          <p>Contagens brutas disponíveis para auditoria.</p>
        </div>
      </div>
      <div className="data-count-grid">
        {Object.entries(data).map(([name, rows]) => (
          <div className="data-count" key={name}>
            <span>{name}</span>
            <strong>{rows.length}</strong>
          </div>
        ))}
      </div>
    </section>
  );
}
